import React from "react";
import {
  Back,
  Button,
  GeneralErrorNotification,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { ISelectedSimCardType } from "../../types";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { useRouter } from "next/router";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { formatAddress, formatPhoneNumber, POSITIVE_DIGITS } from "@nju/utils";
import { changeSimSummaryMachine } from "./changeSimEsimSummary.machine";
import { useMachine } from "@xstate/react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { ChangeSimEsimPeselVerificationError } from "./ChangeSimEsimPeselVerificationError";
import { ChangeSimEsimPeselRegisteredError } from "./ChangeSimEsimPeselRegisteredError";

interface IChangeSimEsimSummaryPageProps {
  selectedSimCardType: ISelectedSimCardType;
}

export function ChangeSimEsimSummaryPage({
  selectedSimCardType,
}: IChangeSimEsimSummaryPageProps) {
  const router = useRouter();
  const params = portalRoutes.mojeKonto.mojaKarta.wymianaKarty.index
    .input(router.query)
    .unwrap();
  const { mobileProducts } = useMemberProducts();
  const { changeSimEsim } = useMemberOrchestrator();
  const clientNumber = mobileProducts.mobileAccess?.characteristic?.msisdn;

  const [{ matches }, send] = useMachine(changeSimSummaryMachine, {
    context: {
      orchestratorId: changeSimEsim?.id,
    },
    actions: {
      onCommitOrderSuccess: () => {
        if (selectedSimCardType === "eSIM") {
          router.push(
            portalRoutes.mojeKonto.mojaKarta.wymianaKarty.esim.zamowienieZlozone.get(
              {
                ...(params?.userId && { userId: params.userId }),
              }
            )
          );
        }

        if (selectedSimCardType === "SIM") {
          router.push(
            portalRoutes.mojeKonto.mojaKarta.wymianaKarty.sim.zamowienieZlozone.get(
              {
                ...(params?.userId && { userId: params.userId }),
              }
            )
          );
        }
      },
      onCancelOrderSuccess: (_, event) => {
        if (
          event.type === "CANCEL_ORDER_SUCCESS" &&
          event.buttonType === "BACK_BUTTON"
        ) {
          router.push(
            portalRoutes.mojeKonto.mojaKarta.wymianaKarty.index.get({
              ...(params?.userId && { userId: params.userId }),
            })
          );
        }

        if (
          event.type === "CANCEL_ORDER_SUCCESS" &&
          event.buttonType === "BACK_BUTTON"
        ) {
          router.push(
            portalRoutes.mojeKonto.mojaKarta.wymianaKarty.index.get({
              ...(params?.userId && { userId: params.userId }),
            })
          );
        }

        if (
          event.type === "CANCEL_ORDER_SUCCESS" &&
          event.buttonType === "BUTTON"
        ) {
          router.push(
            portalRoutes.mojeKonto.mojaKarta.index.get({
              ...(params?.userId && { userId: params.userId }),
            })
          );
        }
      },
    },
  });

  const handleCommitOrder = React.useCallback(() => {
    send({
      type: "COMMIT_ORDER",
    });
  }, [send]);

  const handleCancelOrder = React.useCallback(
    (buttonType: "BUTTON" | "BACK_BUTTON") => {
      send({
        type: "CANCEL_ORDER",
        buttonType,
      });
    },
    [send]
  );

  // to call this error message, pls use PESEL 99111111111 in identity step
  if (matches("onPeselVerificationError")) {
    return <ChangeSimEsimPeselVerificationError userId={params?.userId} />;
  }

  // to call this error message, pls use PESEL 88111111111 in identity step
  if (matches("onPeselRegisteredError")) {
    return <ChangeSimEsimPeselRegisteredError userId={params?.userId} />;
  }

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return window.location.reload();
        }}
      />
    );
  }

  if (changeSimEsim?.state !== "ORDER_SUMMARY") {
    router.push(
      portalRoutes.mojeKonto.mojaKarta.index.get({
        ...(params?.userId && { userId: params.userId }),
      })
    );

    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  return (
    <Panel>
      <Back
        iconColor="accent"
        noPadding={true}
        onClick={() => {
          handleCancelOrder("BACK_BUTTON");
        }}
        text="wstecz"
        textTone="accent"
        type="BUTTON"
      />
      <Separator size="xs" tone="invisible" />
      <Text
        size={{ "@onlyMobile": "keyS", "@fromTablet": "keyM" }}
        weight="bold"
      >
        podsumowanie
      </Text>
      <Separator size="xs" tone="invisible" />
      {selectedSimCardType === "eSIM" && (
        <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
          Zamów i zainstaluj kartę eSIM.{" "}
          <strong>
            {" "}
            Po zatwierdzeniu Twoja obecna karta przestanie działać.
          </strong>{" "}
          W czasie wymiany twój PESEL nie może być zastrzeżony.
        </Text>
      )}
      {selectedSimCardType === "SIM" && (
        <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
          Zamów i zainstaluj kartę SIM. W czasie wymiany twój PESEL nie może być
          zastrzeżony.
        </Text>
      )}
      <Separator size="xs" tone="invisible" />
      <Panel>
        <div className="flex gap-2">
          <div className="flex items-center justify-center rounded-full	w-[30px] h-[30px] bg-cerulean">
            <Icon height={17} name="sim-card" tone="secondary" width={17} />
          </div>
          <div className="w-full">
            <Text
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
              weight="bold"
            >
              {selectedSimCardType === "eSIM" ? " karta eSIM" : "karta SIM"}
              <Separator size="none" tone="invisible" />
            </Text>
            <Text
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
              tone="accent"
              weight="bold"
            >
              {clientNumber &&
                formatPhoneNumber(clientNumber, POSITIVE_DIGITS, " ", "", true)}
            </Text>
            <Separator size="xs" />
            <Text
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
              tone="accent"
              weight="bold"
            >
              0 zł
              <Separator size="none" tone="invisible" />
            </Text>
          </div>
        </div>
      </Panel>
      <Separator size="s" tone="invisible" />
      {selectedSimCardType === "eSIM" && (
        <>
          <div className="flex gap-2 pb-6">
            <div className="flex">
              <Icon height={20} name="info-circle" tone="accent" width={20} />
            </div>
            <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
              Masz zastrzeżony numer PESEL? Wyłącz tę opcję przed potwierdzeniem
              wymiany karty SIM lub zamówieniem dodatkowego SIM-a. Zrobisz to
              najszybciej w aplikacji mObywatel.
            </Text>
          </div>
          <div className="flex gap-2">
            <div className="flex items-center">
              <Icon height={20} name="info-circle" tone="accent" width={20} />
            </div>
            <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
              Pamiętaj, żeby urządzenie, na którym będziesz instalować eSIM
              miało połączenie z Internetem.
            </Text>
          </div>
        </>
      )}
      {selectedSimCardType === "SIM" && changeSimEsim?.delivery && (
        <>
          <Separator size="s" />
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
          >
            adres dostawy karty SIM
          </Text>
          <Separator size="none" tone="invisible" />
          <Text
            size={{
              "@onlyMobile": "m",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            {formatAddress(changeSimEsim.delivery, true)}
          </Text>
          <Separator size="m" />
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
          >
            numer do kontaktu (dla kuriera)
          </Text>
          <Separator size="none" tone="invisible" />
          <Text
            size={{
              "@onlyMobile": "m",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            {formatPhoneNumber(
              changeSimEsim.delivery.contactNumber,
              POSITIVE_DIGITS,
              " ",
              "+48"
            )}
          </Text>
          {changeSimEsim.delivery.note && (
            <>
              <Separator size="xs" tone="invisible" />
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "m",
                }}
              >
                wiadomość dla kuriera
              </Text>
              <Separator size="none" tone="invisible" />
              <Text
                className="break-words"
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                {changeSimEsim.delivery.note}
              </Text>
            </>
          )}
          <div className="flex gap-2 pt-6">
            <div className="flex">
              <Icon height={20} name="info-circle" tone="accent" width={20} />
            </div>
            <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
              Masz zastrzeżony numer PESEL? Wyłącz tę opcję przed potwierdzeniem
              wymiany karty SIM lub zamówieniem dodatkowego SIM-a. Zrobisz to
              najszybciej w aplikacji mObywatel.
            </Text>
          </div>
        </>
      )}
      <Separator size="s" tone="invisible" />
      <div className="flex justify-between only-mobile:flex-col-reverse only-mobile:gap-3">
        <Button
          isLoading={matches("cancelOrder")}
          onPress={() => {
            handleCancelOrder("BUTTON");
          }}
          size={{ "@onlyMobile": "s" }}
          variant="secondary"
        >
          anuluj
        </Button>
        <Button
          isLoading={matches("commitOrder") || matches("success")}
          onPress={handleCommitOrder}
          size={{
            "@onlyMobile": "s",
          }}
          type="submit"
        >
          {selectedSimCardType === "eSIM" && "wymień kartę"}
          {selectedSimCardType === "SIM" && "zamów kartę"}
        </Button>
      </div>
    </Panel>
  );
}
